import React from 'react';
import styled from 'styled-components';

import {MONTHS, DAYS} from '../../lib/timeline';
import Card from '../Card';
import Button from '../Button';
import Icon from '../Icon';

import Pills from './Pills';
import Pill from './Pill';
import Link from './Link';

import {T} from '../../components/I18n';

function Calendar({year, month, height, staticHeight, children, onChange}) {
  const grid = getCalendarGrid(year, month);
  const previousMonth = getPreviousMonth(year, month);
  const nextMonth = getNextMonth(year, month);

  return (
    <Card>
      <CalendarHeader>
        <h3>{MONTHS[month]} <T variable="year">{year}</T></h3>
        <Button tertiary onClick={() => onChange(previousMonth)}>
          <Icon name="chevron-left" />
          {MONTHS[previousMonth[1]]}
        </Button>
        <Button tertiary onClick={() => onChange(nextMonth)}>
        {MONTHS[nextMonth[1]]}
          <Icon name="chevron-right" />
        </Button>
      </CalendarHeader>
      <CalendarDays>
        {new Array(7).fill(null).map((_, i) => (
          <CalendarDay key={`calendar_day_${i}`}>
            {DAYS[i]}
          </CalendarDay>
        ))}
      </CalendarDays>
      <CalendarContainer height={height}>
        {new Array(6).fill(null).map((_, i) => (
          <CalendarRow key={`calendar_row_${i}`} staticHeight={staticHeight}>
            {new Array(7).fill(null).map((_, j) => (
              <CalendarCell
                key={`calendar_cell_${i}_${j}`}
                isCurrentMonth={grid[i][j].getMonth() === month}
                date={grid[i][j]}>
                <CalendarDate isCurrentMonth={grid[i][j].getMonth() === month} date={grid[i][j]}>
                  {grid[i][j].getDate()}
                </CalendarDate>
                <CalendarDateContent>
                  {children(grid[i][j])}
                </CalendarDateContent>
              </CalendarCell>
            ))}
          </CalendarRow>
        ))}
      </CalendarContainer>
    </Card>
  );
}

const CalendarHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #e4e4e4;
  padding: 1rem;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 500;
    flex: 1;
    display: flex;
    align-items: center;
    padding-left: .75rem;
    white-space: pre;
  }
`;

const CalendarDays = styled.div`
  display: flex;
`;

const CalendarDay = styled.div`
  flex: 1;
  font-weight: 500;
  text-align: center;
  border-bottom: 1px solid #e5e5e5;
  border-right: 1px solid #e5e5e5;
  padding: 0.375rem;
`;

const CalendarContainer = styled.div`
  min-height: 40rem;
  height: ${props => props.height ? (props.height.startsWith('calc') ? props.height : `calc(${props.height} - 104px)`) : '40rem'};
  font-size: 0.875rem;
`;

const CalendarRow = styled.div`
  height: ${props => props.staticHeight ? '6.6667rem' : '16.6667%'};
`;

const CalendarCell = styled.div`
  display: inline-flex;
  height: 100%;
  width: 14.2857%;
  border-bottom: 1px solid #e5e5e5;
  border-right: 1px solid #e5e5e5;
  padding: 0.375rem;
  background: ${props => props.isCurrentMonth ? (isToday(props.date) ? '#F6FBFF' : '#fff') : 'rgba(227, 227, 227, 0.25)'};
`;

const CalendarDate = styled.div`
  width: 1.25rem;
  color: ${props => props.isCurrentMonth ? '#484848' : '#747474'};

  ${props => {
    if (isToday(props.date)) {
      return `
        font-weight: bold;
        color: #0049ee;
      `;
    }
  }}
`;

const CalendarDateContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  height: 100%;
`;

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getPreviousMonth(year, month) {
  if (month === 0) {
    return [year - 1, 11];
  }

  return [year, month - 1];
}

function getNextMonth(year, month) {
  if (month === 11) {
    return [year + 1, 0];
  }

  return [year, month + 1];
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function isToday(date) {
  const today = new Date();

  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate();
}

function getCalendarGrid(year, month) {
  const daysThisMonth = getDaysInMonth(year, month);
  const previousMonth = getPreviousMonth(year, month);
  const nextMonth = getNextMonth(year, month);
  const daysLastMonth = getDaysInMonth(...previousMonth);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);
  
  // The first row is special because we need to account for the previous month.
  const firstRow = [];
  
  for (let i = 0; i < firstDayOfMonth; i++) {
    firstRow[i] = new Date(...previousMonth, daysLastMonth - firstDayOfMonth + i + 1);
  }

  for (let i = firstDayOfMonth; i < 7; i++) {
    firstRow[i] = new Date(year, month, i - firstDayOfMonth + 1);
  }

  // Calculate the rest of the rows.
  let nextStartDate = firstRow[6].getDate() + 1;
  let isNextMonth = false;
  const grid = [firstRow];

  for (let i = 1; i < 6; i++) {
    grid[i] = [];

    for (let j = 0; j < 7; j++) {
      const date = nextStartDate + j;

      if (date > daysThisMonth) {
        isNextMonth = true;
        grid[i][j] = new Date(...nextMonth, nextStartDate + j - daysThisMonth);
      } else if (isNextMonth) {
        grid[i][j] = new Date(...nextMonth, nextStartDate + j);
      } else {
        grid[i][j] = new Date(year, month, nextStartDate + j);
      }
    }

    nextStartDate = grid[i][6].getDate() + 1;
  }

  return grid;
}

Calendar.Pills = Pills;
Calendar.Pill = Pill;
Calendar.Link = Link;
export default Calendar;